import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import NoUnits from '../../../../images/no-units-found.svg';
import { getLocation } from '../../../../redux/location/actions/location.action';
import { AppDispatch, IRootState } from '../../../../redux/rootReducer';
import Loader from '../../../common/loader/Loader';
import { AddAvailabilityContext } from '../../../context/AddAvailabilityContext';
import CustomLocationCard from '../../../locations/locationCard/CustomLocationCard';
import { locationsData } from '../../interfaces/Availability.interface';
import styles from './LocationsList.module.css';

interface LocationsListProps {
    type: string;
}

/**
 * Optimized Locations List Component
 * 
 * Features:
 * - Proper error handling
 * - Loading state management
 * - Memoized location data processing
 * - Optimized re-renders
 */
const LocationsList: React.FC<LocationsListProps> = ({ type }) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const addAvailabilityContext = useContext(AddAvailabilityContext);

    const allLocationsList = useSelector((state: IRootState) => state.locationReducer.data.locations);
    const invitedLocations = useSelector((state: IRootState) => state.locationReducer.data?.locationinvitedlist);
    const { loading, errorMsg: error } = useSelector((state: IRootState) => state.locationReducer);

    const [locations, setLocations] = useState<locationsData[]>([]);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    // Note: Fetching is handled by parent component (LocationsForNewLocationAvailability)
    // This component only displays the data from Redux to prevent duplicate API calls

    // Mark as loaded when loading completes
    useEffect(() => {
        if (!loading && (allLocationsList || invitedLocations)) {
            setHasLoadedOnce(true);
        }
    }, [loading, allLocationsList, invitedLocations]);

    // Process and combine locations data
    const processedLocations = useMemo(() => {
        if (!invitedLocations?.length && !allLocationsList?.length) {
            return [];
        }

        const invitedData = invitedLocations?.map((list: locationsData) => ({
            ...list,
            isRestricted: true,
        })) || [];

        return [...invitedData, ...(allLocationsList || [])];
    }, [invitedLocations, allLocationsList]);

    // Update locations state when processed data changes
    useEffect(() => {
        setLocations(processedLocations);
    }, [processedLocations]);

    // Handle location card click
    const handleLocationCard = useCallback((location: locationsData) => {
        try {
            addAvailabilityContext?.setLocationId(location.id || '');
            navigate('/add-availability/units', {
                state: {
                    locationId: location.id,
                    type,
                    locationName: location?.name,
                    lockerType: location?.lockerName,
                    provisionId: location?.provisioningId
                },
            });
        } catch (err) {
            console.error('Error navigating to units:', err);
        }
    }, [addAvailabilityContext, navigate, type]);

    // Show loading state
    if (loading) {
        return <Loader />;
    }

    // Show error state
    if (error) {
        return (
            <div className={styles.errorContainer}>
                <p className={styles.errorText}>
                    Failed to load locations. Please try again later.
                </p>
            </div>
        );
    }

    // Render locations list
    // Only show empty state when loading is complete and there are no locations
    const shouldShowEmptyState = !loading && hasLoadedOnce && locations.length === 0;

    return (
        <div>
            <div className={styles.propertyListing}>
                <div className={styles.locationWrapper}>
                    {locations && locations.length > 0 ? (
                        locations.map((location: locationsData, index: number) => (
                            <div
                                className={`${styles.locationCardActive} ${styles.locationCard}`}
                                key={location.id || index}
                            >
                                <CustomLocationCard
                                    location={location}
                                    handleLocationCard={() => handleLocationCard(location)}
                                />
                            </div>
                        ))
                    ) : shouldShowEmptyState ? (
                        <div className={styles.noUnits}>
                            <img src={NoUnits} alt="no-units" />
                            <p className={styles.noUnitsFoundText}>No Location Found</p>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default LocationsList;

